import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../ui/Icon/Icon';
import PillLink from '../../ui/PillLink/PillLink';
import type { Service, ServiceShowcase } from '../../../data/services';
import { useLanguage } from '../../../i18n/useLanguage';
import type { ServiceCopy, ServiceLabels } from '../../../locales/types';
import styles from './ServiceBlock.module.css';

interface ServiceBlockProps {
    service: Service;
    copy: ServiceCopy;
    labels: ServiceLabels;
    contactHref: string;
}

function ShowcaseChip({ showcase }: { showcase: ServiceShowcase }) {
    const { t } = useTranslation();
    const { localizedPath } = useLanguage();

    switch (showcase.kind) {
        case 'app':
            return (
                <Link to={localizedPath(`/work/${showcase.slug}`)} className={styles.chip}>
                    <img className={styles.chipIcon} src={showcase.iconSrc} alt="" width={32} height={32} loading="lazy" decoding="async" />
                    {showcase.name}
                </Link>
            );
        case 'website':
            return (
                <Link to={localizedPath(`/work/${showcase.slug}`)} className={styles.chip}>
                    <img className={styles.chipWindow} src={showcase.screenshotSrc} alt="" width={44} height={32} loading="lazy" decoding="async" />
                    {showcase.host}
                </Link>
            );
        case 'technology':
            return <span className={`${styles.chip} ${styles.chipText}`}>{showcase.logo.name}</span>;
        case 'aiTask':
            return (
                <Link to={localizedPath('/ai')} className={styles.chip}>
                    <img className={styles.chipIcon} src={showcase.iconSrc} alt="" width={32} height={32} loading="lazy" decoding="async" />
                    {t(`home.services.tasks.${showcase.task}`)}
                </Link>
            );
    }
}

export default function ServiceBlock({ service, copy, labels, contactHref }: ServiceBlockProps) {
    return (
        <article id={service.id} className={styles.block} aria-labelledby={`service-${service.id}`}>
            <div className={styles.head}>
                <h2 id={`service-${service.id}`} className={styles.title}>{copy.title}</h2>
                <p className={styles.lead}>{copy.lead}</p>
                <p className={styles.fit}><strong>{labels.fit}</strong> {copy.fit}</p>
            </div>
            <div className={styles.details}>
                <h3 className={styles.listTitle}>{labels.deliverables}</h3>
                <ul className={styles.list}>
                    {copy.deliverables.map((item) => (
                        <li key={item} className={styles.item}>
                            <Icon name="check" className={styles.check} />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className={styles.footer}>
                <div className={styles.examples}>
                    <h3 className={styles.listTitle}>{labels.examples}</h3>
                    <ul className={styles.proof}>
                        {service.showcase.map((showcase, index) => (
                            <li key={`${showcase.kind}-${index}`}><ShowcaseChip showcase={showcase} /></li>
                        ))}
                    </ul>
                </div>
                <PillLink href={contactHref} variant="outline" icon="arrowRight">{labels.action}</PillLink>
            </div>
        </article>
    );
}
